import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const shortDate = (v) => {
  if (!v) return "";
  const t = String(v).slice(0, 10);
  const M = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const [, m, d] = t.split("-");
  return m ? `${Number(d)} ${M[Number(m) - 1]}` : t;
};

// Shared by employees and contractors so the two never drift into telling
// people different things about the same event.
export default function PayNoticeDialog({
  open, onOpenChange, mode, rows, busy, onConfirm,
}) {
  const paying = mode === "paid";
  const [picked, setPicked] = useState({});
  const [when, setWhen] = useState(new Date().toISOString().slice(0, 10));
  const [alsoEmail, setAlsoEmail] = useState(true);

  // Default to the people who have not been told yet: re-opening after a
  // partial send should not queue the same person up again.
  useEffect(() => {
    if (!open) return;
    const next = {};
    (rows || []).forEach((r) => {
      const told = paying ? r.notified_paid_at : r.notified_ready_at;
      next[r.id] = !told;
    });
    setPicked(next);
  }, [open, rows, paying]);

  const ids = Object.keys(picked).filter((k) => picked[k]);
  const toggle = (id) => setPicked((p) => ({ ...p, [id]: !p[id] }));
  const setAll = (v) => {
    const next = {};
    (rows || []).forEach((r) => { next[r.id] = v; });
    setPicked(next);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg space-y-3" data-testid="pay-notice-dialog">
        <DialogHeader>
          <DialogTitle>
            {paying ? "Record payment" : "Tell them the payslip is ready"}
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-zinc-600">
          {paying
            ? "Marks the month as paid for the people you pick, and emails them that the money has gone out."
            : "Emails the people you pick to say their payslip is available. Says nothing about payment."}
        </p>

        {paying && (
          <div className="flex items-end gap-3">
            <div className="space-y-1">
              <label className="text-xs text-zinc-600">Payment date</label>
              <Input type="date" value={when} className="h-8 w-44"
                     onChange={(e) => setWhen(e.target.value)} />
            </div>
            <label className="flex items-center gap-2 text-xs text-zinc-600 pb-2">
              <input type="checkbox" checked={alsoEmail}
                     onChange={(e) => setAlsoEmail(e.target.checked)} />
              Email them as well
            </label>
          </div>
        )}

        <div className="flex items-center justify-between text-[11px] text-zinc-500">
          <span>{ids.length} of {(rows || []).length} selected</span>
          <span className="flex gap-2">
            <button type="button" className="hover:underline" onClick={() => setAll(true)}>All</button>
            <button type="button" className="hover:underline" onClick={() => setAll(false)}>None</button>
          </span>
        </div>

        <div className="rounded-lg border border-zinc-200 divide-y divide-zinc-100 max-h-72 overflow-y-auto">
          {(rows || []).map((r) => {
            const told = paying ? r.notified_paid_at : r.notified_ready_at;
            return (
              <label key={r.id}
                     className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!picked[r.id]}
                       onChange={() => toggle(r.id)}
                       data-testid={`notice-pick-${r.id}`} />
                <span className="flex-1 min-w-0">
                  <span className="truncate">{r.name}</span>
                  {told && (
                    <span className="text-[11px] text-zinc-400">
                      {" "}already told {shortDate(told)}
                    </span>
                  )}
                  {paying && r.paid_on && (
                    <span className="text-[11px] text-emerald-700">
                      {" · paid "}{shortDate(r.paid_on)}
                    </span>
                  )}
                </span>
                <span className="text-xs tabular-nums text-zinc-600">{inr(r.amount)}</span>
              </label>
            );
          })}
          {!(rows || []).length && (
            <div className="px-3 py-4 text-xs text-zinc-500">
              Nothing to send for this month yet.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" data-testid="pay-notice-confirm"
                  disabled={busy || !ids.length}
                  onClick={() => onConfirm(ids, when, alsoEmail)}>
            {busy
              ? "Working..."
              : paying
                ? `Record payment for ${ids.length}`
                : `Email ${ids.length}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
